import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { parseStepsTarget } from '@/lib/health/stepsTarget';
import { cn } from '@/lib/utils';

interface StepsLinkFieldProps {
  value: number | undefined;
  onChange: (next: number | undefined) => void;
}

/**
 * Steps target as a button plus dialog, mirroring the mobile control
 * (mobile/src/components/StepsLinkButton.tsx).
 *
 * Clicking always opens the dialog — it never unlinks directly. Unlinking is
 * the explicit action inside, so a stray click cannot silently stop a habit
 * auto-completing on the member's phone.
 *
 * Confirming with text `parseStepsTarget` rejects (empty, `0`, non-numeric,
 * Arabic-Indic digits, ...) must never silently clear an existing link either
 * — only the explicit Unlink button may do that. So when the typed text
 * fails to parse and a value was already set, confirm keeps that prior value
 * instead of writing `undefined`.
 *
 * Web sets the target; only the phone ever reads a step count.
 */
export function StepsLinkField({ value, onChange }: StepsLinkFieldProps) {
  const { t, dir } = useLanguage();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');

  const linked = typeof value === 'number' && Number.isFinite(value) && value > 0;

  const openDialog = () => {
    setTyped(linked ? String(value) : '');   // String(), never toLocaleString
    setOpen(true);
  };

  const confirm = () => {
    const parsed = parseStepsTarget(typed);
    // Preserve the existing link on unparsable input; only Unlink may clear it.
    onChange(parsed === undefined && value !== undefined ? value : parsed);
    setOpen(false);
  };

  const unlink = () => {
    onChange(undefined);
    setOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        variant={linked ? 'default' : 'outline'}
        onClick={openDialog}
        className="w-full"
      >
        {linked ? t('habits.stepsLinked', { count: String(value) }) : t('habits.stepsLink')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir={dir}>
          <DialogHeader>
            <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('habits.stepsLinkTitle')}
            </DialogTitle>
          </DialogHeader>

          <Input
            inputMode="numeric"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            dir={dir}
            className={dir === 'rtl' ? 'text-right' : 'text-left'}
          />
          <p className={cn('text-xs text-muted-foreground', dir === 'rtl' ? 'text-right' : 'text-left')}>
            {t('habits.stepsLinkHint')}
          </p>

          <DialogFooter className={cn('gap-2', dir === 'rtl' ? 'flex-row-reverse' : '')}>
            {linked && (
              <Button
                type="button"
                variant="ghost"
                className="text-red-600 hover:text-red-700"
                onClick={unlink}
              >
                {t('habits.stepsUnlink')}
              </Button>
            )}
            <Button type="button" onClick={confirm}>
              {t('habits.stepsLinkConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
